// PLAT-32 AC-2 — open/close state machine for the guest-apply premium-icon tooltips, extracted so the
// tap/hover/focus composition is unit-testable. SFC mounting is unavailable on the outerweb stack
// (vitest node env, no @vitejs/plugin-vue), so the wiring logic lives here as a plain module that both
// guest-apply-form.vue and the spec import. See guest-apply-form.vue.
//
// The touch race this closes: on a tap the browser fires the synthesized pointerenter and focus — either
// of which would OPEN the tooltip — BEFORE the click. A naive click-toggle then flips the just-opened
// tooltip shut, so a tap can never open it. On touch these handlers ignore the synthesized hover/focus
// and let the click be the single authority; on mouse and keyboard, hover and focus still drive it.

const isSyntheticTouch = (event) =>
  !!(event && event.pointerType && event.pointerType !== 'mouse');

export function createPremiumIconTooltip() {
  return {
    // Key of the open tooltip (period.id + '-' + idx, or 'summary-' + idx), or null when none is open.
    openKey: null,
    // A touch/pen pointerdown sets this so the focus that immediately follows the tap does NOT pre-open
    // the tooltip and lose the toggle to the click. Cleared when focus leaves.
    suppressHoverFocus: false,

    // Hover open/close — mouse only. pointerenter/leave carry pointerType and fire before pointerdown,
    // so the touch guard is read straight off the event.
    pointerEnter(key, event) {
      if (isSyntheticTouch(event)) return;
      this.openKey = key;
    },
    // Close only the tooltip this icon owns: the mouse leaving icon B must not close a
    // keyboard-focused tooltip A that the pointer never touched.
    pointerLeave(key, event) {
      if (isSyntheticTouch(event)) return;
      if (this.openKey === key) this.openKey = null;
    },

    // Fires before focus; records whether the interaction about to focus+click is a touch/pen tap.
    pointerDown(event) {
      this.suppressHoverFocus = isSyntheticTouch(event);
    },

    // Keyboard focus opens; a pointer-driven focus is suppressed and left to the click.
    focus(key) {
      if (this.suppressHoverFocus) return;
      this.openKey = key;
    },
    // Close only the tooltip this icon owns: a different icon losing focus must not close a
    // mouse-hovered tooltip A.
    blur(key) {
      if (this.openKey === key) this.openKey = null;
      this.suppressHoverFocus = false;
    },

    // Tap/click is the single authority on touch: toggle this key.
    click(key) {
      this.openKey = this.openKey === key ? null : key;
    },

    // Outside tap/click (the document listener / Alpine @click.away equivalent) closes any open tooltip.
    closeAway() {
      this.openKey = null;
    },
  };
}
